import { useCallback, useRef, useState } from "react";
import { startProductCheckout } from "../api/checkout";

/**
 * Starting a payment, from any call-to-action.
 *
 * Two presentations of the same flow:
 *   mode "modal"    — Keepz's picker in an iframe, the buyer never leaves the page
 *   mode "redirect" — full-page hand-off, the historical behaviour
 *
 * No method is chosen here — the buyer picks on Keepz's page. The server may still
 * answer that a checkout can't be framed, in which case this redirects instead.
 *
 * Either way the order is created server-side first, so a buyer who closes the modal
 * or loses the tab can still complete it — the reconciliation sweep settles whatever
 * happens afterwards.
 *
 * Products that turn out to be free never reach Keepz; `onFree` runs instead so the
 * caller can fall through to the normal join flow. A promo code that takes off the
 * whole price doesn't reach Keepz either: the server grants access there and then, so
 * that is reported as paid.
 *
 * @param {{ mode?: "modal"|"redirect", onFree?: () => void, onError?: (message: string, code?: string) => void, onPaid?: (order: object) => void }} options
 */
/**
 * Keepz's checkout page has a fixed minimum width and clips instead of reflowing,
 * so there is no honest way to frame it on a phone. Narrow viewports redirect.
 */
const MODAL_MIN_VIEWPORT = 760;

export default function useCheckout({
  mode = "modal",
  onFree,
  onError,
  onPaid,
} = {}) {
  const [starting, setStarting] = useState(false);
  const [order, setOrder] = useState(null); // { checkoutUrl, orderId, amount, currency }
  /** Orders whose payment has been handled. @see handlePaid */
  const paidOrdersRef = useRef(new Set());

  /**
   * @param {string} mintSpaceId
   * @param {string} productId
   * @param {{ method?: string, payer?: object }} [choice]
   * @param {string} [promoCode]
   * @returns {Promise<boolean>} whether checkout started
   */
  const start = useCallback(
    async (mintSpaceId, productId, choice, promoCode, bookingId) => {
      if (starting) return false;
      setStarting(true);
      try {
        const result = await startProductCheckout(mintSpaceId, productId, choice, promoCode, bookingId);

        if (result?.free) {
          onFree?.();
          return true;
        }
        if (result?.granted) {
          onPaid?.(result);
          return true;
        }
        if (!result?.checkoutUrl) {
          throw new Error("No checkout URL was returned");
        }

        const tooNarrow =
          typeof window !== "undefined" && window.innerWidth < MODAL_MIN_VIEWPORT;
        // A bank's open-banking login sends X-Frame-Options, so it can only be a page.
        if (mode === "redirect" || tooNarrow || result.frameable === false) {
          // Nothing after this runs — the browser returns to /checkout/return.
          window.location.href = result.checkoutUrl;
          return true;
        }
        setOrder(result);
        return true;
      } catch (err) {
        const message =
          err?.response?.data?.message ||
          err?.message ||
          "Could not start checkout. Please try again.";
        // The refusal's code too, so a page can word it in the reader's language.
        onError?.(message, err?.response?.data?.code);
        return false;
      } finally {
        setStarting(false);
      }
    },
    [starting, mode, onFree, onError, onPaid]
  );

  /** Close the modal. The order stays open at Keepz until it's paid or expires. */
  const close = useCallback(() => setOrder(null), []);

  /**
   * The payment went through: close the checkout and let the page react — once.
   *
   * Leaving the modal open with a paid order is what looped: the page's refresh
   * unmounted the modal, it mounted again on the same order, saw it paid, and asked
   * for another refresh. A handled order is dropped here, so there is nothing left
   * to re-open, and a second report of the same payment is ignored.
   */
  const handlePaid = useCallback(
    (data) => {
      const orderId = data?.orderId || order?.orderId;
      if (orderId) {
        if (paidOrdersRef.current.has(orderId)) return;
        paidOrdersRef.current.add(orderId);
      }
      setOrder(null);
      onPaid?.(data);
    },
    [onPaid, order?.orderId]
  );

  return { start, starting, order, close, handlePaid, isOpen: Boolean(order) };
}
